import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { Thermometer, Droplets, Wind, Download, TrendingUp, BarChart3, Brain } from 'lucide-react';
import { format, subDays } from 'date-fns';
import ProductionForecast from './ProductionForecast';
import AIProductionForecast from './AIProductionForecast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RoomAnalytics = ({ showProductionForecast = true }) => {
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [temperatureData, setTemperatureData] = useState([]);
  const [wateringData, setWateringData] = useState([]);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [analyticsTab, setAnalyticsTab] = useState('forecast');

  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/rooms`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setRooms(response.data);
        if (response.data.length > 0) {
          setSelectedRoom(response.data[0].id);
        }
      } catch (error) {
        toast.error('Failed to fetch rooms');
      } finally {
        setLoading(false);
      }
    };
    fetchRooms();
  }, []);

  useEffect(() => {
    if (!selectedRoom) return;

    const fetchRoomData = async () => {
      try {
        const token = localStorage.getItem('token');
        const startDate = subDays(new Date(), days).toISOString();
        
        const [tempRes, waterRes] = await Promise.all([
          axios.get(`${API}/temperature-logs?room_id=${selectedRoom}&start_date=${startDate}`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API}/watering/logs?room_id=${selectedRoom}&start_date=${startDate}`, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => ({ data: { logs: [] } }))
        ]);

        // /api/temperature-logs returns { logs, total } — unwrap
        const tempLogs = Array.isArray(tempRes.data)
          ? tempRes.data
          : (tempRes.data?.logs || []);

        // Process temperature data
        const tempData = tempLogs.map(log => {
          const avgCompost = log.compost_readings
            ? log.compost_readings.reduce((sum, r) => sum + r.temperature, 0) / log.compost_readings.length
            : 0;
          return {
            date: format(new Date(log.log_date), 'MMM dd HH:mm'),
            air_temp: log.air_temperature,
            compost_avg: parseFloat(avgCompost.toFixed(1)),
            co2: log.co2_level,
            shift: log.shift
          };
        }).reverse();

        // Process watering data
        const waterLogsData = waterRes.data?.logs || waterRes.data || [];
        const waterData = (Array.isArray(waterLogsData) ? waterLogsData : []).map(log => ({
          date: format(new Date(log.log_date), 'MMM dd'),
          water: log.water_given_liters
        })).reverse();

        // Group water by date
        const groupedWater = {};
        waterData.forEach(w => {
          if (!groupedWater[w.date]) {
            groupedWater[w.date] = { date: w.date, water: 0 };
          }
          groupedWater[w.date].water += w.water;
        });

        setTemperatureData(tempData);
        setWateringData(Object.values(groupedWater));
      } catch (error) {
        console.error('Failed to fetch room data:', error);
      }
    };

    fetchRoomData();
  }, [selectedRoom, days]);

  const handleExport = async () => {
    if (!selectedRoom) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/export/room-analytics/${selectedRoom}?format=excel&days=${days}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `room_analytics_${selectedRoom}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('Export downloaded!');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const selectedRoomData = rooms.find(r => r.id === selectedRoom);

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Loading rooms...</div>;
  }

  return (
    <div className="space-y-6" data-testid="room-analytics">
      {/* Analytics Tabs - Only shown for Owner */}
      {showProductionForecast ? (
        <Tabs value={analyticsTab} onValueChange={setAnalyticsTab} className="w-full">
          <TabsList className="grid w-full max-w-lg grid-cols-3 mb-6">
            <TabsTrigger value="forecast" className="flex items-center gap-2" data-testid="tab-forecast">
              <TrendingUp className="w-4 h-4" />
              Production Forecast
            </TabsTrigger>
            <TabsTrigger value="ai-forecast" className="flex items-center gap-2" data-testid="tab-ai-forecast">
              <Brain className="w-4 h-4" />
              AI Forecast
            </TabsTrigger>
            <TabsTrigger value="room-stats" className="flex items-center gap-2" data-testid="tab-room-stats">
              <BarChart3 className="w-4 h-4" />
              Room Analytics
            </TabsTrigger>
          </TabsList>

          <TabsContent value="forecast">
            <ProductionForecast />
          </TabsContent>

          <TabsContent value="ai-forecast">
            <AIProductionForecast />
          </TabsContent>

          <TabsContent value="room-stats">
            <RoomStatsContent
              rooms={rooms}
              selectedRoom={selectedRoom}
              setSelectedRoom={setSelectedRoom}
              days={days}
              setDays={setDays}
              temperatureData={temperatureData}
              wateringData={wateringData}
              handleExport={handleExport}
            />
          </TabsContent>
        </Tabs>
      ) : (
        /* Grower - No tabs, just room analytics */
        <RoomStatsContent
          rooms={rooms}
          selectedRoom={selectedRoom}
          setSelectedRoom={setSelectedRoom}
          days={days}
          setDays={setDays}
          temperatureData={temperatureData}
          wateringData={wateringData}
          handleExport={handleExport}
        />
      )}
    </div>
  );
};

// Extracted Room Stats Content Component
const RoomStatsContent = ({ rooms, selectedRoom, setSelectedRoom, days, setDays, temperatureData, wateringData, handleExport }) => {
  const selectedRoomData = rooms.find(r => r.id === selectedRoom);
  
  return (
    <div className="space-y-6">
      {/* Room Selector & Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Room Wise Analytics</span>
            <div className="flex items-center gap-3">
              {/* Days Filter */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Last:</span>
                {[7, 14, 30].map(d => (
                  <Button
                    key={d}
                    variant={days === d ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setDays(d)}
                    className={days === d ? 'bg-[#1A4D2E]' : ''}
                  >
                    {d}d
                  </Button>
                ))}
              </div>
              
              {/* Export Button */}
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleExport}
                data-testid="export-room-analytics"
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Room Selector Grid */}
          <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
            {rooms.map(room => (
              <Button
                key={room.id}
                variant={selectedRoom === room.id ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedRoom(room.id)}
                className={`${selectedRoom === room.id ? 'bg-[#1A4D2E]' : ''}`}
                data-testid={`room-btn-${room.room_number}`}
              >
                {room.name || `Room ${room.room_number}`}
              </Button>
            ))}
          </div>

          {/* Current Room Info */}
          {selectedRoomData && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-lg">{selectedRoomData.name}</h3>
                <p className="text-sm text-gray-600">
                  Status: <Badge className={selectedRoomData.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                    {selectedRoomData.status}
                  </Badge>
                </p>
              </div>
              <div className="flex gap-4">
                {selectedRoomData.temperature && (
                  <div className="text-center">
                    <Thermometer className="w-5 h-5 mx-auto text-red-500" />
                    <p className="text-lg font-bold">{selectedRoomData.temperature}°C</p>
                    <p className="text-xs text-gray-500">Temp</p>
                  </div>
                )}
                {selectedRoomData.co2_level && (
                  <div className="text-center">
                    <Wind className="w-5 h-5 mx-auto text-blue-500" />
                    <p className="text-lg font-bold">{selectedRoomData.co2_level}</p>
                    <p className="text-xs text-gray-500">CO2</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Temperature Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Thermometer className="w-5 h-5 text-red-500" />
            Temperature History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {temperatureData.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No temperature data available for this room in the selected period.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={temperatureData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 12 }} domain={['auto', 'auto']} />
                <Tooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="air_temp" 
                  stroke="#3B82F6" 
                  strokeWidth={2} 
                  name="Air Temp (°C)" 
                  dot={false}
                />
                <Line 
                  type="monotone" 
                  dataKey="compost_avg" 
                  stroke="#EF4444" 
                  strokeWidth={2} 
                  name="Avg Compost Temp (°C)" 
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* CO2 Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wind className="w-5 h-5 text-blue-500" />
            CO2 Levels
          </CardTitle>
        </CardHeader>
        <CardContent>
          {temperatureData.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No CO2 data available for this room in the selected period.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={temperatureData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 12 }} domain={['auto', 'auto']} />
                <Tooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="co2" 
                  stroke="#10B981" 
                  strokeWidth={2} 
                  name="CO2 (ppm)" 
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Watering History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Droplets className="w-5 h-5 text-cyan-500" />
            Watering History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {wateringData.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No watering data available for this room in the selected period.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={wateringData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} label={{ value: 'Liters', angle: -90, position: 'insideLeft' }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="water" fill="#06B6D4" name="Water (L)" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RoomAnalytics;
